/**
 * StaffHome — Home operatore Sarabi Staff (CRM v7).
 *
 * Griglia di TILE grandi. Nessuna sidebar admin. Nessun contenuto config.
 * Estendibile: aggiungere un elemento al `TILES` per un nuovo modulo.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, Utensils, Users, MapPin, PackageSearch, MessageSquare, Layers, Clock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { useMessagingPoll } from "@/lib/useMessagingPoll";

const TILES = [
  { to: "/staff/agenda",        key: "agenda",        title: "Agenda",
    desc: "Prenotazioni, ricerca, nuovo", Icon: CalendarClock, perm: "staff.agenda" },
  { to: "/staff/servizio",      key: "servizio",      title: "Servizio",
    desc: "Turno in corso · 1-click", Icon: Utensils, perm: "staff.service" },
  { to: "/staff/clienti",       key: "clienti",       title: "Clienti",
    desc: "Ricerca CRM operativa", Icon: Users, perm: "staff.clients" },
  { to: "/staff/lists",         key: "lists",         title: "Liste",
    desc: "Segmenti CRM per invii mirati", Icon: Layers, perm: "staff.clients" },
  { to: "/staff/sala",          key: "sala",          title: "Sala Live",
    desc: "Occupazione tavoli", Icon: MapPin, perm: "staff.live_room" },
  { to: "/staff/disponibilita", key: "disponibilita", title: "Disponibilità",
    desc: "Prodotti esauriti / disponibili", Icon: PackageSearch, perm: "staff.availability" },
  { to: "/staff/messages",      key: "messages",      title: "Messaggi",
    desc: "Inbox WhatsApp cliente", Icon: MessageSquare,
    perm: "staff.messaging", withUnreadBadge: true },
  { to: "/staff/scheduled-templates", key: "scheduled-templates", title: "Invii programmati",
    desc: "Programma e gestisci gli invii WhatsApp", Icon: Clock,
    perm: "staff.messaging.templates.send" },
];

export default function StaffHome() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);

  const showMessages = hasPermission(user, "staff.messaging");

  // M2.2.2: usa hook condiviso con polling 12s + cache-buster + focus refresh.
  // Totale aggregato lato server (tutte le conversazioni, non solo le prime 100).
  useMessagingPoll(
    "/staff/messaging/whatsapp/unread-summary",
    {
      intervalMs: 12000,
      enabled: showMessages,
      onData: (data) => setUnread(data?.unread_total || 0),
    },
  );

  const visibleTiles = TILES.filter((t) => !t.perm || hasPermission(user, t.perm));

  return (
    <div data-testid="staff-home-page" className="max-w-5xl mx-auto">
      <div className="mb-8">
        <div className="eyebrow">Servizio</div>
        <h1 className="font-serif text-4xl mt-1">Buon lavoro.</h1>
        <p className="text-sm text-ink-500 mt-2">Cosa vuoi fare adesso?</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleTiles.map(({ to, key, title, desc, Icon, withUnreadBadge }) => (
          <button key={key} onClick={() => nav(to)}
                    data-testid={`home-tile-${key}`}
                    className="group text-left rounded-3xl border-2 border-sand-300 bg-white
                                p-6 min-h-[160px] hover:border-bronze-500 hover:shadow-lg
                                transition-all active:scale-[0.98] relative">
            {withUnreadBadge && unread > 0 && (
              <span data-testid="tile-messages-unread-badge"
                    className="absolute top-4 right-4 min-w-[26px] h-[26px] px-2
                                rounded-full bg-bronze-600 text-white text-xs
                                font-semibold flex items-center justify-center">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-bronze-50 border border-bronze-200
                                flex items-center justify-center flex-shrink-0
                                group-hover:bg-bronze-100 transition-colors">
                <Icon className="w-7 h-7 text-bronze-700" strokeWidth={1.5} />
              </div>
              <div>
                <div className="font-serif text-2xl">{title}</div>
                <div className="text-sm text-ink-500 mt-1">{desc}</div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
